import React, { useEffect, useRef, useState } from 'react'
import { Building2, Check, ChevronDown, Lock } from 'lucide-react'
import { Company, COMPANY_LABELS } from '../../api/chatService'

interface CompanySelectorProps {
  value: Company
  options: Company[]
  onChange: (company: Company) => void
  // Once a chat has started its company is fixed; the selector shows it but can't
  // be changed.
  disabled?: boolean
}

// Company picker for the composer, like an LLM's model selector. It chooses which
// company a NEW chat talks to; after the first message it locks to that company.
const CompanySelector: React.FC<CompanySelectorProps> = ({
  value,
  options,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  if (options.length < 2) return null

  // Locked: static indicator of the chat's company, no dropdown.
  if (disabled) {
    return (
      <div
        className="company-selector company-selector--locked"
        title="The company is set for this chat and can't be changed."
      >
        <Building2 size={14} className="company-selector__icon" />
        <span className="company-selector__label">{COMPANY_LABELS[value]}</span>
        <Lock size={12} className="company-selector__lock" />
      </div>
    )
  }

  return (
    <div className="company-selector" ref={containerRef}>
      <button
        type="button"
        className="company-selector__trigger"
        onClick={() => setIsOpen(open => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select company for this chat"
      >
        <Building2 size={15} className="company-selector__icon" />
        <span className="company-selector__label">{COMPANY_LABELS[value]}</span>
        <ChevronDown
          size={15}
          className={`company-selector__chevron ${
            isOpen ? 'company-selector__chevron--open' : ''
          }`}
        />
      </button>

      {isOpen && (
        <ul className="company-selector__menu" role="listbox">
          {options.map(option => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === value}
                className={`company-selector__option ${
                  option === value ? 'company-selector__option--selected' : ''
                }`}
                onClick={() => {
                  onChange(option)
                  setIsOpen(false)
                }}
              >
                <span>{COMPANY_LABELS[option]}</span>
                {option === value && <Check size={14} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CompanySelector
